import { useTranslate } from "@tolgee/react";
import { CircleAlert, Info, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { Button } from "#/components/ui/button";
import {
	Dialog,
	DialogClose,
	DialogCloseButton,
	DialogContent,
	DialogDescription,
	DialogTitle,
	DialogTrigger,
} from "#/components/ui/dialog";
import { useAppSettings } from "#/hooks/use-app-settings";
import { useMyReferralStats } from "../hooks/use-my-referral-stats";
import {
	BUILDER_FEE_PERCENT,
	computeRatios,
	REFERRAL_PRODUCTS,
} from "../lib/config";
import { buildInviteLink } from "../lib/share";

interface Props {
	code: string;
}

export function ReferralFeeDialog({ code }: Props) {
	const { t } = useTranslate();
	const stats = useMyReferralStats();
	const settings = useAppSettings();

	const bps =
		stats.data?.revenue_share_bps ??
		settings.data?.default_revenue_share_bps ??
		0;
	const referralRate = BUILDER_FEE_PERCENT * (bps / 10_000);
	const { rows, maxRatio, maxKey } = computeRatios(
		referralRate,
		REFERRAL_PRODUCTS,
	);
	const maxRounded = Math.floor(maxRatio);

	function handleCopyLink() {
		const link = buildInviteLink(code);
		navigator.clipboard
			.writeText(link)
			.then(() => toast.success(t("referrals.fee.link-copied")))
			.catch(() => {});
	}

	return (
		<Dialog>
			<DialogTrigger asChild>
				<Button variant="ghost" size="sm" className="h-8 gap-1.5 -ml-2">
					<Info className="h-3.5 w-3.5" />
					{t("referrals.fee.view-details")}
				</Button>
			</DialogTrigger>

			<DialogContent className="flex max-h-[calc(100dvh-1rem)] w-[min(520px,calc(100vw-1rem))] flex-col overflow-hidden rounded-[22px] border border-trade-border-subtle bg-[linear-gradient(180deg,var(--trade-surface-overlay),var(--trade-surface-overlay-end))] p-0 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03),0_36px_72px_rgba(0,0,0,0.52)] sm:max-h-[calc(100dvh-2rem)] sm:w-[min(520px,calc(100vw-2rem))]">
				{/* Header */}
				<div className="flex items-start justify-between gap-4 px-5 pt-5 sm:px-7 sm:pt-7">
					<div className="min-w-0">
						<span className="inline-block rounded-full border border-trade-green/50 bg-trade-green/10 px-3 py-1.5 text-label2-medium text-trade-green">
							{t("referrals.fee.badge")}
						</span>
						<DialogTitle className="mt-3 text-title1 sm:text-display4 text-trade-text-strong">
							{t("referrals.fee.title", { max: String(maxRounded) })}
						</DialogTitle>
						<DialogDescription className="mt-2 text-body2 text-trade-text-secondary">
							{t("referrals.fee.subtitle")}
						</DialogDescription>
					</div>
					<DialogCloseButton />
				</div>

				{/* Scrollable body */}
				<div className="min-h-0 overflow-y-auto px-5 pb-5 sm:px-7 sm:pb-7">
					{/* Hero card */}
					<div className="mt-5 rounded-[16px] border border-trade-accent bg-[linear-gradient(180deg,color-mix(in_srgb,var(--color-theme-primary-normal)_8%,transparent)_0%,color-mix(in_srgb,var(--color-theme-primary-normal)_3%,transparent)_100%)] p-6 text-center shadow-[0_0_0_1px_color-mix(in_srgb,var(--color-theme-primary-normal)_14%,transparent)]">
						<p className="text-display2 text-[var(--color-theme-primary-normal)]">
							~{maxRatio.toFixed(1)}%
						</p>

						<p className="mt-3 text-label1 text-trade-text-secondary">
							{t("referrals.fee.hero-desc")}
						</p>
						<p className="mt-1.5 text-label2 text-trade-text-secondary/70">
							{t("referrals.fee.hero-fine", {
								max: referralRate,
							})}
						</p>
					</div>

					{/* Product tier table */}
					<div className="mt-5">
						<h3 className="flex items-center gap-1.5 text-label1-bold uppercase text-trade-text-secondary">
							<LayoutGrid className="h-3.5 w-3.5" />
							{t("referrals.fee.section-title")}
						</h3>

						<div className="mt-3 overflow-hidden rounded-xl border border-trade-border-subtle bg-trade-surface-inset/50">
							{/* Table header */}
							<div className="grid grid-cols-[1.5fr_1fr_1fr] bg-trade-surface/60 px-4 py-2.5 text-caption1-medium uppercase text-trade-text-secondary">
								<div>{t("referrals.fee.col-product")}</div>
								<div>{t("referrals.fee.col-taker")}</div>
								<div>{t("referrals.fee.col-maker")}</div>
							</div>

							{/* Rows */}
							{rows.map(({ product, takerRatio, makerRatio }) => {
								const isTakerMax = maxKey === `${product.nameKey}-taker`;
								const isMakerMax = maxKey === `${product.nameKey}-maker`;

								return (
									<div
										key={product.nameKey}
										className={`grid grid-cols-[1.5fr_1fr_1fr] items-center border-t border-trade-border-subtle/60 px-4 py-3 text-label1 ${product.highlight ? "bg-[var(--color-theme-status-positive)]/[0.04]" : ""}`}
									>
										<div>
											<p className="text-label1-medium text-trade-text-strong">
												{t(product.nameKey)}
											</p>
											<p className="mt-0.5 text-caption1 text-trade-text-secondary">
												{t(product.descriptionKey)}
											</p>
										</div>
										<RatioCell ratio={takerRatio} isMax={isTakerMax} />
										<RatioCell ratio={makerRatio} isMax={isMakerMax} />
									</div>
								);
							})}
						</div>
					</div>

					{/* Info note */}
					<div className="mt-4 flex gap-2.5 rounded-xl bg-trade-surface-inset/40 p-3.5">
						<CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-trade-text-secondary" />
						<p className="text-label2 text-trade-text-secondary">
							{t("referrals.fee.info-note", {
								rate: String(referralRate),
							})}
						</p>
					</div>

					{/* Footer */}
					<div className="mt-5 flex gap-2.5">
						<DialogClose asChild>
							<Button
								variant={"secondary"}
								className="inline-flex min-h-12 flex-1 items-center justify-center"
							>
								{t("referrals.fee.dismiss")}
							</Button>
						</DialogClose>
						<Button
							onClick={handleCopyLink}
							className="inline-flex min-h-12 flex-1 items-center justify-center text-[var(--color-theme-static-white)]"
						>
							{t("referrals.fee.copy-link")}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}

function RatioCell({ ratio, isMax }: { ratio: number; isMax: boolean }) {
	return (
		<p
			className={
				isMax
					? "text-label1-bold text-trade-green"
					: "text-label1-medium text-trade-text-strong"
			}
		>
			{ratio.toFixed(1)}%{isMax ? " ⚡" : ""}
		</p>
	);
}
